import React, { memo, ReactNode } from "react";

import { Modal } from "@UI/Components/Modal/Modal";
import { VStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

import messages from "../../messages";

export interface ITaskCreationExitModalProps {
  isOpen: boolean;
  /** "Continue my request": dismiss and stay where the visitor was. */
  onClose: () => void;
  /** "Save request and quit": let the blocked navigation through. */
  onQuit: () => void;
}

const boldFormatter = (...chunks: ReactNode[]) => (
  <Text as="b" color="gray.800">
    {chunks}
  </Text>
);

/**
 * Asks before a click takes the visitor out of the creation flow (RT-4321).
 * Driven by `useFlowExitGuard`, which owns the blocked destination — this
 * component only knows the two answers.
 */
export const TaskCreationExitModal = memo(
  ({ isOpen, onClose, onQuit }: ITaskCreationExitModalProps) => (
    <Modal isOpen={isOpen} onClose={onClose}>
      <VStack spacing={4} textAlign="center">
        <Heading size="md">
          <FormattedMessage {...messages["taskCreationExitModal.title"]} />
        </Heading>
        <Text color="gray.600" align="left">
          <FormattedMessage
            {...messages["taskCreationExitModal.body"]}
            values={{ b: boldFormatter }}
          />
        </Text>
        <VStack spacing={3} width="100%" paddingTop="8px">
          <Button width="100%" colorScheme="seaGreen" onClick={onClose}>
            <FormattedMessage
              {...messages["taskCreationExitModal.continueButton"]}
            />
          </Button>
          <Button
            width="100%"
            colorScheme="gray"
            variant="secondary"
            onClick={onQuit}
          >
            <FormattedMessage
              {...messages["taskCreationExitModal.quitButton"]}
            />
          </Button>
        </VStack>
      </VStack>
    </Modal>
  )
);

TaskCreationExitModal.displayName = "TaskCreationExitModal";
